import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";

import { getItem, getProducts, PRODUCTS_PAGE_SIZE } from "features/Products/api";

/**
 * Серверный поиск товаров для селектов.
 * База очень большая, поэтому:
 * - без поиска показываем первую страницу;
 * - при вводе ищем на сервере (debounce 450мс);
 * - выбранный товар догружаем отдельно, чтобы он всегда был в options.
 */
export function useItemSearchOptions(search, selectedItemId) {
  const trimmed = (search || "").trim();
  const [debounced, setDebounced] = useState(trimmed);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(trimmed), 450);
    return () => clearTimeout(t);
  }, [trimmed]);

  const selectedId =
    selectedItemId !== undefined && selectedItemId !== null && selectedItemId !== ""
      ? Number(selectedItemId)
      : null;

  const selectedQuery = useQuery(
    ["item-selected", selectedId],
    () => getItem(selectedId),
    { enabled: typeof selectedId === "number" && !Number.isNaN(selectedId) }
  );

  const listQuery = useQuery(
    ["item-search-options", debounced],
    () =>
      getProducts({ page: 1, pageSize: PRODUCTS_PAGE_SIZE, search: debounced }),
    { keepPreviousData: true }
  );

  const items = useMemo(() => {
    const listItems = listQuery.data?.items ?? [];
    const selectedItem = selectedQuery.data;
    if (!selectedItem) return listItems;

    const map = new Map();
    [selectedItem, ...listItems].forEach((i) => map.set(String(i.itemid), i));
    return Array.from(map.values());
  }, [listQuery.data, selectedQuery.data]);

  const options = useMemo(
    () =>
      items.map((i) => ({
        value: String(i.itemid),
        label: i.longname || i.name,
      })),
    [items]
  );

  return {
    items,
    options,
    isLoading:
      listQuery.isLoading ||
      listQuery.isFetching ||
      selectedQuery.isLoading ||
      selectedQuery.isFetching,
  };
}
